<template>
  <div>
    <div class="p-4">
      <h2 class="text-xl font-bold mb-4">Backups</h2>
      <InlineMessage severity="info" class="mb-4">
       Backups only include the AI.Lab Main Tenant and not any users, visits, or reports from other tenants.
      </InlineMessage>
      <br>
      <Button label="Generate Backup" icon="pi pi-save" class="p-button-success mb-4" @click="generateBackup" :disabled="restoring" />
      <DataTable
        :value="availableBackups"
        class="min-w-full bg-white border border-gray-200 mb-4"
        :emptyMessage="'No backups available.'"
      >
        <Column field="name" header="Backup Name">
          <template #body="slotProps">
        {{ slotProps.data }}
          </template>
        </Column>
        <Column header="Download">
          <template #body="slotProps">
        <a
          @click="downloadZip(slotProps.data)"
          class="text-blue-600 hover:underline"
          download
        >
          Download
        </a>
          </template>
        </Column>
      </DataTable>
    </div>
    <div class="p-4">
      <h2 class="text-xl font-bold mb-4">Restore from Backup</h2>

      <div class="mb-4">
        <Dropdown v-model="selectedBackup" :options="availableBackups" placeholder="Select a backup" class="w-full" />
      </div>

      <Button label="Restore Selected Backup" icon="pi pi-refresh" class="p-button-warning"
        :disabled="!selectedBackup || restoring" @click="restoreBackup" />

      <p v-if="restoreMessage" class="mt-3 text-green-500">
        {{ restoreMessage }}
      </p>
      <p v-if="restoreError" class="mt-3 text-red-500">
        {{ restoreError }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import axiosInstance from '@/plugins/axios';


const availableBackups = ref<string[]>([]);
const selectedBackup = ref<string | null>(null);
const restoreMessage = ref('');
const restoreError = ref('');
const restoring = ref(false);

const loadBackups = async () => {
  try {
    const res = await axiosInstance.get('testing/backups');
    availableBackups.value = res.data.dumps;
  } catch (err) {
    restoreError.value = 'Failed to load backups.';
  }
};

const generateBackup = async () => {
  restoring.value = true;
  restoreMessage.value = '';
  restoreError.value = '';
  try {
    const res = await axiosInstance.post('testing/generateBackup');
    restoreMessage.value = res.data.message;
    await loadBackups(); 
  } catch (err: any) {
    restoreError.value = err.response?.data?.error || 'Backup generation failed.';
  } finally {
    restoring.value = false;
  }
};

const restoreBackup = async () => {
  if (!selectedBackup.value) return;
  restoring.value = true;
  restoreMessage.value = '';
  restoreError.value = '';
  try {
    const res = await axiosInstance.post('testing/restore', {
      folder: selectedBackup.value,
    });
    restoreMessage.value = res.data.message;
  } catch (err: any) {
    restoreError.value = err.response?.data?.error || 'Restore failed.';
  } finally {
    restoring.value = false;
  }
};
const downloadZip = async (backupName: string) => {
  const res = await axiosInstance.get(`testing/downloadDumpAsZip/${backupName}`, {
    responseType: 'blob',
  });
  const blob = new Blob([res.data], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${backupName}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

onMounted(loadBackups);
</script>
